import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TaskBulkCreateSameInputObject,
  TaskStatusEnum,
  TaskUpdateInputObject,
  FilterGroupingEnum,
} from 'graphql-common';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import toast from '@lib/components/Toast/Toast';
import { Values } from '@lib/interfaces/form';
import { PAGINATION_PARAMS } from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';
import useAssetsQueryHook from 'utils/fetch/hooks/useAssetsQueryHook';
import useSiteAreasQueryHook from 'utils/fetch/hooks/useSiteAreasQueryHook';
import useMutations from 'routes/Task/utils/useMutations';
import useTaskQueries from 'routes/Task/utils/useTaskQueries';
import getData from 'routes/Task/utils/getData';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { FieldNames } from 'routes/Task/enums';
import getPreparedTaskDataFromValues from './getPreparedTaskDataFromValues';
import getPreparedTaskScheduleDataFromValues from './getPreparedTaskScheduleDataFromValues';
import {
  selectAreaModal,
  unselectAreaModal,
  selectAssetModal,
  unselectAssetModal,
} from '../modals';

interface UseActionsProps {
  closeArchiveModal?: () => void;
  closeScheduleModal?: () => void;
  closeTaskDeleteModal?: () => void;
  formRef?: FormRefType;
  isScheduleModalOpened?: boolean;
  openArchiveModal?: () => void;
  setSelectedSiteAreaIds?: (v: { [key: string]: string }) => void;
  setSelectedSiteId?: (v?: string) => void;
  useModalManagementResult?: UseModalManagementResult;
}

export default function useActions(props: UseActionsProps) {
  const {
    closeArchiveModal,
    closeScheduleModal,
    closeTaskDeleteModal,
    isScheduleModalOpened,
    openArchiveModal,
    setSelectedSiteAreaIds,
    setSelectedSiteId,
    useModalManagementResult,
    formRef,
  } = props;
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToTaskFormView = () => {
    navigate(
      APP_URLS.dashboard.tasks.formView.getDynamicPath({
        pathParts: { id: `${id}` },
      }),
    );
  };

  // Task
  const { taskQuery, taskFormTemplateQuery, fullTaskQuery, taskScheduleQuery } =
    useTaskQueries({
      isScheduleModalOpened,
    });

  // Mutations
  const useMutationsResult = useMutations({
    closeScheduleModal,
    taskQueryRefetch: taskQuery?.refetch,
    taskScheduleQueryRefetch: taskScheduleQuery?.refetch,
  });
  const {
    createMutation,
    createMutationResult,
    destroyTaskMutation,
    taskBulkSchedulingMutation,
    taskBulkSchedulingResult,
    taskBulkUpdateStatusMutation,
    taskBulkUpdateStatusResult,
    updateMutation,
    updateMutationResult,
  } = useMutationsResult;

  const {
    createMutationError,
    createTaskLoading,
    fullTaskData,
    fullTaskError,
    fullTaskLoading,
    taskBulkSchedulingError,
    taskBulkSchedulingLoading,
    taskBulkUpdateStatusError,
    taskBulkUpdateStatusLoading,
    taskData,
    taskDataForFormTemplate,
    taskDataSiteAreaId,
    taskDataSiteId,
    taskError,
    taskFormTemplate,
    taskFormTemplateError,
    taskFormTemplateId,
    taskFormTemplateLoading,
    taskFormTemplateVersion,
    taskFormTemplateVersionId,
    taskLoading,
    taskScheduleData,
    taskScheduleError,
    taskScheduleLoading,
    updateMutationError,
    updateTaskLoading,
  } = getData({
    createMutationResult,
    fullTaskQuery,
    taskBulkSchedulingResult,
    taskBulkUpdateStatusResult,
    taskFormTemplateQuery,
    taskQuery,
    taskScheduleQuery,
    updateMutationResult,
  });
  const { fetchIds: fetchSiteAreaIds } = useSiteAreasQueryHook({
    skip: true,
    includeSiteIds: true,
    paginationParams: PAGINATION_PARAMS,
  });
  const { fetchIds: fetchAssetIds } = useAssetsQueryHook({
    skip: true,
    includeSiteIds: true,
    paginationParams: PAGINATION_PARAMS,
  });
  const sitesForApproverRequestIdRef = useRef(0);

  const setSitesForApproverField = (siteIds: string[]) => {
    if (!formRef?.current) return;
    const uniqueSiteIds = Array.from(
      new Set(siteIds.filter((siteId): siteId is string => Boolean(siteId))),
    );
    const normalizedSites: Values[] = uniqueSiteIds.map((siteId) => ({
      value: siteId,
    }));
    formRef.current.setValue(
      FieldNames.sitesForApprover,
      normalizedSites.length ? normalizedSites : [],
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );
  };

  const fetchSitesForApprover = (
    ids: string[],
    fetchFn?:
      | ReturnType<typeof useSiteAreasQueryHook>['fetchIds']
      | ReturnType<typeof useAssetsQueryHook>['fetchIds'],
  ) => {
    const normalizedIds = ids.filter((item): item is string => Boolean(item));
    sitesForApproverRequestIdRef.current += 1;
    const requestId = sitesForApproverRequestIdRef.current;

    if (!normalizedIds.length || !fetchFn) {
      setSitesForApproverField([]);
      return;
    }

    const lazyFetch = fetchFn as unknown as (options?: {
      variables?: {
        filters?: Record<string, unknown>[];
        onlyIds?: boolean;
        includeSiteId?: boolean;
      };
    }) => Promise<{
      data?: { data?: { collection?: Array<{ siteId?: string | null }> } };
    }>;

    lazyFetch({
      variables: {
        filters: [
          {
            id: {
              grouping: FilterGroupingEnum.Or,
              predicate: {
                in: normalizedIds,
              },
            },
          },
        ],
        onlyIds: true,
        includeSiteId: true,
      },
    })
      .then((response) => {
        if (sitesForApproverRequestIdRef.current !== requestId) return;
        const collection =
          response?.data?.data?.collection ??
          ([] as Array<{ siteId?: string | null }>);
        const siteIds = collection
          .map((item) => item?.siteId)
          .filter((siteId): siteId is string => Boolean(siteId));
        setSitesForApproverField(siteIds);
      })
      .catch(() => {
        if (sitesForApproverRequestIdRef.current !== requestId) return;
        setSitesForApproverField([]);
      });
  };

  const onCreate = (values: Values) => {
    createMutation({
      variables: {
        attributes: getPreparedTaskDataFromValues(
          values,
          undefined,
        ) as TaskBulkCreateSameInputObject,
      },
    });
  };

  const onUpdate = (values: Values) => {
    if (id) {
      updateMutation({
        variables: {
          id,
          attributes: getPreparedTaskDataFromValues(
            values,
            taskData,
          ) as TaskUpdateInputObject,
        },
      });
    }
  };

  const onDestroyHandler = () => {
    if (id) {
      destroyTaskMutation({
        variables: { id },
      });
    }
  };

  const onConfirmedDelete = () => {
    if (closeTaskDeleteModal) closeTaskDeleteModal();
    toast({
      content: t('destroy-task-success'),
      closeCallback: onDestroyHandler,
    });
  };

  const onEditHandler = () => {
    navigate(
      APP_URLS.dashboard.tasks.edit.getDynamicPath({
        pathParts: {
          id: `${id}`,
        },
      }),
    );
  };

  const onDuplicateHandler = () => {
    navigate(
      APP_URLS.dashboard.tasks.duplicate.getDynamicPath({
        pathParts: {
          id: `${id}`,
        },
      }),
    );
  };

  const onRescheduleHandler = (values?: Values) => {
    if (id && values) {
      taskBulkSchedulingMutation({
        variables: {
          ids: [id],
          attributes: getPreparedTaskScheduleDataFromValues(values),
        },
      });
    }
  };

  const onArchiveHandler = () => {
    if (id) {
      taskBulkUpdateStatusMutation({
        variables: {
          ids: [id],
          attributes: { status: TaskStatusEnum.Archived },
        },
      });
    }
  };

  const onStatusChangeHandler = (idValue: string, status: TaskStatusEnum) => {
    if (idValue && status) {
      taskBulkUpdateStatusMutation({
        variables: { ids: [idValue], attributes: { status } },
      });
    }
  };

  const onBulkArchiveOpen = () => {
    if (id && openArchiveModal) {
      openArchiveModal();
    }
  };

  const onConfirmedBulkArchive = () => {
    if (closeArchiveModal) closeArchiveModal();
    toast({
      content: t('archive-task-success'),
      closeCallback: onArchiveHandler,
    });
  };

  const onOpenEditTaskDetails = () => {
    navigate(
      APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
        pathParts: {
          id: taskFormTemplateId,
          versionId: taskFormTemplateVersionId,
        },
      }),
    );
  };

  const onCreateNewFormTemplate = () => {
    window.open(APP_URLS.dashboard.formTemplates.add.path, '_blank');
  };

  const onViewFormTemplate = (
    formTemplateId: string,
    formTemplateVersionId: string,
  ) => {
    window.open(
      APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
        pathParts: {
          id: formTemplateId,
          versionId: formTemplateVersionId,
        },
      }),
      '_blank',
    );
  };

  const onCloseScheduleModal = () => {
    if (closeScheduleModal) closeScheduleModal();
    taskBulkSchedulingResult.reset();
  };

  const openAreasModalTableMultiselect = () => {
    if (useModalManagementResult) {
      useModalManagementResult.openModal(selectAreaModal);
    }
  };
  const openAssetsModalTableMultiselect = () => {
    if (useModalManagementResult) {
      useModalManagementResult.openModal(selectAssetModal);
    }
  };
  const openAreasModalTableMultiunselect = () => {
    if (useModalManagementResult) {
      useModalManagementResult.openModal(unselectAreaModal);
    }
  };
  const openAssetsModalTableMultiunselect = () => {
    if (useModalManagementResult) {
      useModalManagementResult.openModal(unselectAssetModal);
    }
  };

  const onCloseAreasModal = () => {
    if (useModalManagementResult) {
      useModalManagementResult.closeModal(selectAreaModal);
      useModalManagementResult.closeModal(unselectAreaModal);
    }
  };
  const onCloseAssetsModal = () => {
    if (useModalManagementResult) {
      useModalManagementResult.closeModal(selectAssetModal);
      useModalManagementResult.closeModal(unselectAssetModal);
    }
  };

  const onUpdateMultiselect = (v: string[], fieldName: string) => {
    if (formRef?.current) {
      formRef.current.setValue(
        fieldName,
        v?.length ? v.map((idValue) => ({ value: idValue })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  };

  const onResetAppoverField = () => {
    if (formRef?.current) {
      formRef.current.setValue(FieldNames.approver, null, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      formRef.current.setValue(FieldNames.sitesForApprover, []);
    }
  };

  const onAreasMultiselect = (v: string[]) => {
    onUpdateMultiselect(v, FieldNames.siteArea);
    onUpdateMultiselect([], FieldNames.asset);
    onResetAppoverField();
    fetchSitesForApprover(v, fetchSiteAreaIds);
    if (v?.length === 0) {
      onCloseAreasModal();
    }
  };

  const onAssetsMultiselect = (v: string[]) => {
    onUpdateMultiselect(v, FieldNames.asset);
    onUpdateMultiselect([], FieldNames.siteArea);
    onResetAppoverField();
    fetchSitesForApprover(v, fetchAssetIds);
    if (v?.length === 0) {
      onCloseAssetsModal();
    }
  };

  useEffect(() => {
    if (taskDataSiteId && setSelectedSiteId) {
      setSelectedSiteId(taskDataSiteId);
    }
  }, [setSelectedSiteId, taskDataSiteId]);

  useEffect(() => {
    if (taskDataSiteAreaId && setSelectedSiteAreaIds) {
      setSelectedSiteAreaIds({ 'assignations.0.siteArea': taskDataSiteAreaId });
    }
  }, [setSelectedSiteAreaIds, taskDataSiteAreaId]);

  return {
    createMutationError,
    createTaskLoading,
    fullTaskData,
    fullTaskError,
    fullTaskLoading,
    idParam: id,
    navigateToTaskFormView,
    onAreasMultiselect,
    onAssetsMultiselect,
    onBulkArchiveOpen,
    onCloseAreasModal,
    onCloseAssetsModal,
    onCloseScheduleModal,
    onConfirmedBulkArchive,
    onConfirmedDelete,
    onCreate,
    onCreateNewFormTemplate,
    onDuplicateHandler,
    onEditHandler,
    onOpenEditTaskDetails,
    onRescheduleHandler,
    onStatusChangeHandler,
    onUpdate,
    onViewFormTemplate,
    openAreasModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiselect,
    openAssetsModalTableMultiunselect,
    taskBulkSchedulingError,
    taskBulkSchedulingLoading,
    taskBulkUpdateStatusError,
    taskBulkUpdateStatusLoading,
    taskData: taskData || taskDataForFormTemplate,
    taskError,
    taskFormTemplate,
    taskFormTemplateError,
    taskFormTemplateLoading,
    taskFormTemplateVersion,
    taskLoading,
    taskScheduleData,
    taskScheduleError,
    taskScheduleLoading,
    updateMutationError,
    updateTaskLoading,
  };
}
